import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { topics } from "../../constants/topics";
import { useTopic } from "../../context/topicContext";
import { ReactComponent as SendIcon } from "../../assets/send.svg";
import { ReactComponent as DoubleChevronLeft } from "../../assets/double-chevron-left.svg";
import { ReactComponent as DoubleChevronRight } from "../../assets/double-chevron-right.svg";
import AnswerComponent from "../../components/Answer";
import UserQuestion from "../../components/UserQuestion";
import ChatOptions from "../../components/ChatOptions";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  createQuestionThunk,
  getDetailHistoryThunk,
} from "../../store/actions/chat.action";
import { HeaderContext } from "../MainLayout";
import { useNewChat } from "../../context/newChatContext";
import { useParams } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import FlightResults from "../Flight/FlightResults";
import HotelResults from "../Hotel/HotelResults";
import MerchantResults from "../Merchant/MerchantResults";
import { APIProvider, Marker, Map } from "@vis.gl/react-google-maps";
import axios from "axios";
import { getMerchantsNearlyThunk } from "../../store/actions/journey.action";
import AuthGuard from '../../components/AuthGuard';

interface ChatStep {
  isReplying?: boolean;
  content: string;
  type: "user" | "bot";
  options?: Array<{ id: number; text: string; type: string }>;
  payload?: any;
  booking_type: 1 | 2 | 3 | null;
}

interface ResponseDataHotel {
  departStartDate?: string | null;
  departEndDate?: string | null;
  brands?: string[] | null;
  hotel?: {
    city?: string | null;
    country?: string | null;
    name?: string | null;
  };
  next_message?: string;
  result_message?: string;
  confirm_message?: string;
}

interface IOption {
  id: number;
  text: string;
  type: string;
}

interface FormDataFlight {
  departFrom:
    | {
        code: string;
        name: string;
      }[]
    | null;
  departTo:
    | {
        code: string;
        name: string;
      }[]
    | null;
  departEndDate: string | null;
  departStartDate: string | null;
  cabinClass: string | null;
  totalTicket: number;
}

interface FormDataHotel {
  ci: string | null;
  co: string | null;
  brands: string[] | null;
  city: string | null;
  country: string | null;
  name: string | null;
}

interface FormDataMerchant {
  lat: string | null;
  lng: string | null;
  country: string | null;
  merchantName: string | null;
}

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useAppDispatch();
  const { setTopic } = useTopic();
  const { setBackAction, setIsRightSideOpen, setHeaderTitle } =
    React.useContext(HeaderContext);
  const { me } = useAppSelector((state) => state.authReducer);
  const { isNewChat } = useNewChat();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<ChatStep[]>([]);
  const [sendingMessageLoading, setSendingMessageLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [payload, setPayload] = useState<any>(null);
  const [chatId, setChatId] = useState<string | null>(null);
  const [detailChat, setDetailChat] = useState<any[]>([]);
  const [formDataFlight, setFormDataFlight] = useState<FormDataFlight>({
    departFrom: null,
    departTo: null,
    departEndDate: null,
    departStartDate: null,
    cabinClass: null,
    totalTicket: 1,
  });
  const [formDataHotel, setFormDataHotel] = useState<FormDataHotel>({
    ci: null,
    co: null,
    brands: null,
    city: null,
    country: null,
    name: null,
  });
  const [formDataMerchant, setFormDataMerchant] = useState<FormDataMerchant>({
    lat: null,
    lng: null,
    country: null,
    merchantName: null,
  });
  const [bookingType, setBookingType] = useState<1 | 2 | 3 | null>(null);
  const sendingMessage = useRef<boolean>();
  const chatContentRef = useRef<HTMLDivElement>(null);
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isOpenMapView, setIsOpenMapView] = useState(false);
  const [merchantLocation, setMerchantLocation] = useState<{
    lat: number;
    lng: number;
  }>();

  const getCurrentLocation = async () => {
    try {
      const response = await axios.get("https://reallyfreegeoip.org/json/");
      return response.data;
    } catch (error) {
      console.error("Error fetching country:", error);
      return {
        country_name: "",
        latitude: 0,
        longitude: 0,
      };
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const updateFormDataFlight = (response: FormDataFlight) => {
    setFormDataFlight((prevFormData: any) => {
      const updatedFormData = { ...prevFormData };

      (Object.keys(response) as Array<keyof FormDataFlight>).forEach((key) => {
        if (!!response[key] && response[key] !== prevFormData[key]) {
          updatedFormData[key] = response[key] as FormDataFlight[typeof key];
        }
      });

      return updatedFormData;
    });
  };

  const updateFormDataHotel = (response: ResponseDataHotel) => {
    setFormDataHotel((prevFormData) => {
      const updatedFormData: FormDataHotel = { ...prevFormData };
      // Update ci (start date) and co (end date)
      if (
        response.departStartDate !== null &&
        response.departStartDate !== undefined
      ) {
        updatedFormData.ci = response.departStartDate;
      }
      if (
        response.departEndDate !== null &&
        response.departEndDate !== undefined
      ) {
        updatedFormData.co = response.departEndDate;
      }

      // Update brands
      if (response.brands !== null && response.brands !== undefined) {
        updatedFormData.brands = response.brands;
      }

      // Update hotel information
      if (response.hotel) {
        if (response.hotel.city !== null && response.hotel.city !== undefined) {
          updatedFormData.city = response.hotel.city;
        }
        if (
          response.hotel.country !== null &&
          response.hotel.country !== undefined
        ) {
          updatedFormData.country = response.hotel.country;
        }
        if (response.hotel.name !== null && response.hotel.name !== undefined) {
          updatedFormData.name = response.hotel.name;
        }
      }

      return updatedFormData;
    });
  };

  const updateFormDataMerchant = (response: any) => {
    setFormDataMerchant((prevFormData) => {
      const updatedFormData: FormDataMerchant = { ...prevFormData };

      updatedFormData.lat =
        response?.geoData?.geometry?.location?.lat ||
        response?.currentLocation?.latitude;
      updatedFormData.lng =
        response?.geoData?.geometry?.location?.lng ||
        response?.currentLocation?.longitude;
      updatedFormData.country =
        response?.location?.country || response?.currentLocation?.country_name;
      updatedFormData.merchantName = response?.merchantName;

      return updatedFormData;
    });
  };

  const handleFetchFlightResult = async (option: IOption) => {
    try {
      setTimeout(async () => {
        const updatedHistoryMessage = [
          ...messages,
          { content: option.text, type: "user" } as ChatStep,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];

        const payload = {
          departFrom: formDataFlight.departFrom
            ? formDataFlight.departFrom[0].code
            : "",
          departTo: formDataFlight.departTo
            ? formDataFlight.departTo[0].code
            : "",
          cabinClass: formDataFlight.cabinClass || "Economy",
          departStartDate: formDataFlight.departStartDate || "",
          departEndDate: formDataFlight.departEndDate || "",
        };

        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          type: "bot",
          content: "Great! Here are the available options for you",
          payload: payload,
          booking_type: bookingType,
        };
        await dispatch(
          createQuestionThunk({
            question: JSON.stringify({
              ...payload,
              result_message: "Great! Here are the available options for you",
            }),
            chat_id: chatId,
            need_bot: false,
            bot_role: "assistant",
            booking_type: bookingType,
          })
        ).unwrap();
        setMessages(updatedHistoryMessage);
      }, 1200);
    } catch (e) {}
  };

  const handleFetchHotelResult = async (option: IOption) => {
    try {
      setTimeout(async () => {
        const updatedHistoryMessage = [
          ...messages,
          { content: option.text, type: "user" } as ChatStep,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];
        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          type: "bot",
          content: "Great! Here are the available options for you",
          payload: formDataHotel,
          booking_type: bookingType,
        };
        await dispatch(
          createQuestionThunk({
            question: JSON.stringify({
              ...formDataHotel,
              result_message: "Great! Here are the available options for you",
            }),
            chat_id: chatId,
            need_bot: false,
            bot_role: "assistant",
            booking_type: bookingType,
          })
        ).unwrap();
        setMessages(updatedHistoryMessage);
      }, 1200);
    } catch (e) {}
  };

  const handleFetchMerchantResult = async (option: IOption) => {
    try {
      setTimeout(async () => {
        const updatedHistoryMessage = [
          ...messages,
          { content: option.text, type: "user" } as ChatStep,
          { isReplying: true, content: "", type: "bot" } as ChatStep,
        ];
        updatedHistoryMessage[updatedHistoryMessage.length - 1] = {
          type: "bot",
          content: "Great! Here are the available options for you",
          payload: formDataMerchant,
          booking_type: bookingType,
        };
        await dispatch(
          createQuestionThunk({
            question: JSON.stringify({
              ...formDataMerchant,
              result_message: "Great! Here are the available options for you",
            }),
            chat_id: chatId,
            need_bot: false,
            bot_role: "assistant",
            booking_type: bookingType,
          })
        ).unwrap();
        setMessages(updatedHistoryMessage);
      }, 1200);
    } catch (e) {}
  };

  const handleSendMessage = async (message?: string) => {
    const messageToSend = message || question;
    if (messageToSend.trim() === "" || sendingMessage.current) return;

    // Remove options from the last message if it exists
    setMessages((prevMsg) => {
      const updatedMessages = [...prevMsg];
      const lastMessage = updatedMessages[updatedMessages.length - 1];
      if (lastMessage) {
        lastMessage.options = [];
      }
      return updatedMessages;
    });

    setSendingMessageLoading(true);
    sendingMessage.current = true;

    const userMessageObject: ChatStep = {
      isReplying: false,
      content: messageToSend,
      type: "user",
      options: [],
      payload: null,
      booking_type: bookingType,
    };

    try {
      setMessages((prevMsg) => [...prevMsg, userMessageObject]);
      if (!message) {
        setQuestion("");
      }

      // Add loading message
      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          {
            content: "",
            isReplying: true,
            type: "bot",
            booking_type: bookingType,
          },
        ]);
      }, 600);

      const res = await dispatch(
        createQuestionThunk({
          question: messageToSend,
          need_bot: true,
          bot_role: "user",
          chat_id: chatId,
          booking_type: bookingType,
        })
      ).unwrap();

      setBookingType(res?.booking_type);

      if (res?.booking_type === 1) {
        updateFormDataFlight(res);
      }

      if (res?.booking_type === 2) {
        updateFormDataHotel(res);
      }

      let userLocation = null;
      if (!res.location) {
        userLocation = await getCurrentLocation();
      }

      if (res?.booking_type === 3) {
        updateFormDataMerchant({ ...res, currentLocation: userLocation });
      }

      if (res?.chat_id) {
        setChatId(res.chat_id);
      }

      // Update the loading message with the actual response
      setMessages((prevMsg) => {
        const updatedMessages = [...prevMsg];
        updatedMessages[updatedMessages.length - 1] = {
          content:
            res.next_message || res.confirm_message || res.result_message || "",
          isReplying: false,
          type: "bot",
          options: res.confirm_message
            ? [
                {
                  id: 1,
                  text: "Yes, the details are correct",
                  type: "confirm",
                },
                {
                  id: 2,
                  text: "No, I want to modify the details",
                  type: "modify",
                },
              ]
            : [],
          payload: res.result_message ? res : null,
          booking_type: res.booking_type,
        };
        return updatedMessages;
      });
    } catch (error) {
      console.error("Error sending message:", error);
      // Handle error state
      setMessages((prevMsg) => {
        const updatedMessages = [...prevMsg];
        updatedMessages[updatedMessages.length - 1] = {
          content: "Sorry, there was an error processing your request.",
          isReplying: false,
          type: "bot",
          booking_type: bookingType,
        };
        return updatedMessages;
      });
    } finally {
      sendingMessage.current = false;
      setSendingMessageLoading(false);
    }
  };

  const handleSelectOption = async (
    option: IOption,
    booking_type: 1 | 2 | 3 | null
  ) => {
    setMessages((prevMsg) => [
      ...prevMsg,
      { content: option.text, type: "user", booking_type: bookingType },
    ]);

    if (option.type === "confirm") {
      await dispatch(
        createQuestionThunk({
          question: option.text,
          chat_id: chatId,
          need_bot: false,
          bot_role: "user",
          booking_type: bookingType,
        })
      ).unwrap();

      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          {
            content: "",
            isReplying: true,
            type: "bot",
            booking_type: bookingType,
          },
        ]);
      }, 600);

      if (booking_type === 1) {
        handleFetchFlightResult(option);
      }

      if (booking_type === 2) {
        handleFetchHotelResult(option);
      }

      if (booking_type === 3) {
        handleFetchMerchantResult(option);
      }
    }

    if (option.type === "modify") {
      await dispatch(
        createQuestionThunk({
          question: option.text,
          chat_id: chatId,
          need_bot: false,
          bot_role: "user",
          booking_type: bookingType,
        })
      ).unwrap();

      setTimeout(() => {
        setMessages((prevMsg) => [
          ...prevMsg,
          {
            content:
              "Got it! Just tell us the new details, and we'll update them for you",
            type: "bot",
            booking_type: bookingType,
          },
        ]);
      }, 400);

      await dispatch(
        createQuestionThunk({
          question: JSON.stringify({
            next_message:
              "Got it! Just tell us the new details, and we'll update them for you",
          }),
          chat_id: chatId,
          need_bot: false,
          bot_role: "assistant",
          booking_type: bookingType,
        })
      );
    }
  };

  const handleSeeMerchantResult = async (payload: FormDataMerchant) => {
    try {
      const res = await dispatch(
        getMerchantsNearlyThunk({
          lat: Number(payload?.lat)!,
          lng: Number(payload?.lng)!,
          country: payload?.country!,
          merchantName: payload?.merchantName!,
          types: [],
          placeId: "",
        })
      ).unwrap();
      setPayload({
        mcc: res?.data?.data?.mccCode,
        merchantName: res?.data?.data?.merchantName,
        merchantLocation: res?.data?.data?.streetAddress,
        country: res?.data?.data?.countryCode || payload?.country,
        typeOfTransaction: res?.data?.data?.mccCategory,
        booking_type: 3,
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleTopicSelect = (topic: any) => {
    localStorage.setItem("topic", topic.id.toString());
    setTopic(localStorage.getItem("topic") || "");

    // Send default message based on topic
    const defaultMessages = {
      1: "I want to book a flight",
      2: "I want to book a hotel",
      3: "I want to find a merchant",
    };

    handleSendMessage(
      defaultMessages[topic.id as keyof typeof defaultMessages]
    );
  };

  const fetchChatContent = async () => {
    if (!params?.id) {
      setDetailChat([]);
      return;
    }

    try {
      const res = await dispatch(
        getDetailHistoryThunk({ id: params.id })
      ).unwrap();
      setDetailChat(res.fullMsg);
    } catch (error) {
      console.error("Error fetching chat content:", error);
    }
  };

  const processDetailChat = async (detailChat: any[]): Promise<ChatStep[]> => {
    // Find the most recent message with a booking type
    const lastMessageWithBookingType = [...detailChat]
      .reverse()
      .find((msg) => msg.booking_type);
    setBookingType(lastMessageWithBookingType?.booking_type || null);

    const processedChat: ChatStep[] = await Promise.all(
      detailChat?.map(async (item): Promise<ChatStep> => {
        if (item.role === "user") {
          return {
            type: "user",
            content: item.content[0].text,
            options: [],
            payload: null,
            booking_type: item.booking_type,
          };
        } else if (item.role === "assistant") {
          let parsedContent: any = {};
          try {
            parsedContent = JSON.parse(item.content[0].text);
          } catch (error) {
            console.error("Error parsing assistant content:", error);
          }

          const question =
            parsedContent.next_message ||
            parsedContent.confirm_message ||
            parsedContent.result_message ||
            item.content[0].text;

          let payload = null as any;

          if (parsedContent.result_message && item.booking_type === 3) {
            payload = {
              lat: parsedContent.lat || "",
              lng: parsedContent.lng || "",
              country: parsedContent.country || "",
              merchantName: parsedContent.merchantName || "",
            };
          }

          if (parsedContent.result_message && item.booking_type === 1) {
            payload = {
              departFrom: parsedContent.departFrom || "",
              departTo: parsedContent.departTo || "",
              cabinClass: parsedContent.cabinClass || "Economy",
              departStartDate: parsedContent.departStartDate || "",
              departEndDate: parsedContent.departEndDate || "",
            };
          }

          if (parsedContent.result_message && item.booking_type === 2) {
            payload = {
              ci: parsedContent.ci || "",
              co: parsedContent.co || "",
              brands: parsedContent.brands || [],
              city: parsedContent.city || "",
              country: parsedContent.country || "",
              name: parsedContent.name || "",
            };
          }

          return {
            type: "bot",
            content: question,
            payload: payload,
            booking_type: item.booking_type,
          };
        }

        return {
          type: "bot",
          content: "",
          options: [],
          payload: null,
          booking_type: item.booking_type,
        };
      })
    );
    return processedChat;
  };

  const handleTextareaChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setQuestion(event.target.value);
    // Reset height to auto to get the correct scrollHeight
    event.target.style.height = "auto";
    // Set the height to scrollHeight to fit the content
    event.target.style.height = `${event.target.scrollHeight}px`;
  };

  const getResultTitle = () => {
    if (!payload) return "";
    switch (payload.booking_type) {
      case 1:
        return "Flights";
      case 2:
        return "Hotels";
      case 3:
        return "Merchants";
      default:
        return "";
    }
  };

  const renderResults = () => {
    if (!payload) return null;
    switch (payload.booking_type) {
      case 1:
        return <FlightResults payload={payload} />;
      case 2:
        return <HotelResults payload={payload} />;
      case 3:
        return <MerchantResults payload={payload} />;
      default:
        return null;
    }
  };

  const onOpenMapPopup = (location: { lat: number; lng: number }) => {
    setMerchantLocation(location);
    setIsMapVisible(true);
    setIsOpenMapView(true);
  };

  const onCloseMapPopup = () => {
    setIsOpenMapView(false);
    setTimeout(() => {
      setIsMapVisible(false);
      setMerchantLocation(undefined);
    }, 300);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (chatContentRef.current) {
      chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    setChatId(params?.id || "");
    fetchChatContent();
  }, [params]);

  useEffect(() => {
    const updateHistory = async () => {
      const processedChat = await processDetailChat(detailChat);
      if (processedChat.length) {
        setMessages(processedChat);
      } else {
        setMessages([]);
      }
    };

    updateHistory();
  }, [detailChat]);

  useEffect(() => {
    if (isNewChat) {
      setMessages([]);
      setChatId(null);
    }
  }, [isNewChat]);

  useEffect(() => {
    setBackAction?.(() => {
      return () => navigate("/");
    });
    if (detailChat?.[0]?.content?.[0]?.text && !!params?.id) {
      setHeaderTitle?.(detailChat?.[0]?.content?.[0]?.text);
    } else {
      setHeaderTitle?.("New Chat");
    }

    return () => {
      setBackAction?.(undefined);
      setHeaderTitle?.(undefined);
    };
  }, [navigate, setBackAction, detailChat]);

  useEffect(() => {
    setIsRightSideOpen?.(isSidebarOpen);
  }, [isSidebarOpen]);

  return (
    <Wrapper>
      {me && (
        <LeftSidebar>
          <Sidebar />
        </LeftSidebar>
      )}
      <WrapperMainContent isSidebarOpen={isSidebarOpen} isAuth={!!me}>
        <MainContent>
          {messages.length === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                paddingTop: "15svh",
                paddingBottom: "5svh",
              }}
            >
              <Title>How can I help you today?</Title>
              <Description>
                This chatbot helps users find the best flights, hotels, and
                merchants with personalized recommendations and seamless booking
                options.
              </Description>

              {/* <FeatureCards>
              {topics.map((topic) => (
                <Card key={topic.id} onClick={() => handleTopicSelect(topic)}>
                  <h3>{topic.title}</h3>
                  <div style={{ height: 30 }}>
                    <img src={topic.image} alt="" />
                  </div>
                  <p>{topic.subText}</p>
                </Card>
              ))}
            </FeatureCards> */}
            </div>
          ) : null}

          {messages.length > 0 && (
            <ChatContent ref={chatContentRef}>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <AnswerComponent
                    type={msg.type}
                    isReplying={msg.isReplying}
                    content={msg.content}
                    options={msg.options}
                  />
                  {msg.options && msg.options.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        margin: "0 8px 0 48px",
                        gap: 12,
                      }}
                    >
                      {msg.options.map((option) => (
                        <Option
                          key={option.id}
                          onClick={() =>
                            handleSelectOption(option, msg.booking_type)
                          }
                        >
                          {option.text}
                        </Option>
                      ))}
                    </div>
                  )}
                  {msg.payload && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        margin: "0 8px 0 48px",
                        gap: 12,
                      }}
                    >
                      <SeeResults
                        onClick={() => {
                          if (msg.booking_type !== 3) {
                            setPayload({
                              ...msg.payload,
                              booking_type: msg.booking_type,
                            });
                          }
                          if (msg.booking_type === 3) {
                            handleSeeMerchantResult(msg.payload);
                          }
                          setIsSidebarOpen(true);
                        }}
                      >
                        See Results
                      </SeeResults>
                      {msg.booking_type === 3 &&
                        msg.payload.lat &&
                        msg.payload.lng && (
                          <SeeResults
                            onClick={() =>
                              onOpenMapPopup({
                                lat: msg.payload.lat,
                                lng: msg.payload.lng,
                              })
                            }
                          >
                            Open on Map
                          </SeeResults>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </ChatContent>
          )}

          <AuthGuard>
            <ChatInputContainer>
              <ChatInput
                value={question}
                onChange={handleTextareaChange}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    e.shiftKey == false &&
                    !sendingMessage.current
                  ) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Hey Kardo!"
                rows={1}
                readOnly={!me}
              />
              <SendButton
                onClick={() => handleSendMessage()}
                disabled={!question || sendingMessageLoading || !me}
              >
                <SendIcon width={20} style={{ paddingLeft: 2 }} />
              </SendButton>
            </ChatInputContainer>
          </AuthGuard>
        </MainContent>
      </WrapperMainContent>
      <RightSidebar isOpen={isSidebarOpen}>
        <HeaderRightbar>
          <div style={{ position: "absolute" }}>
            {isSidebarOpen && (
              <DoubleChevronRight width={14} onClick={toggleSidebar} />
            )}
          </div>
          <h3 style={{ margin: 0, textAlign: "center", width: "100%" }}>
            {getResultTitle()}
          </h3>
        </HeaderRightbar>
        {payload && renderResults()}
      </RightSidebar>
      {isMapVisible && (
        <Overlay onClick={onCloseMapPopup} isOpen={isOpenMapView}>
          <Modal onClick={(e) => e.stopPropagation()} isOpen={isOpenMapView}>
            <MinimizeButton onClick={onCloseMapPopup}>↙</MinimizeButton>
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY!}>
              <Map
                disableDefaultUI={true}
                defaultCenter={merchantLocation}
                defaultZoom={14}
                mapId={"95f882a45d3a706"}
                style={{ height: "80vh", width: "80vw" }}
              >
                <Marker position={merchantLocation} />
              </Map>
            </APIProvider>
          </Modal>
        </Overlay>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: calc(100svh - 80px);
`;

const LeftSidebar = styled.div`
  display: flex;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const WrapperMainContent = styled.div<{
  isSidebarOpen: boolean;
  isAuth: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: calc(100% - ${(props) => (props.isSidebarOpen ? "480px" : "0px")} - ${(props) => (props.isAuth ? "280px" : "0px")});
  transition: width 0.3s ease-in-out;
  box-sizing: border-box;

  @media (max-width: 1300px) {
    width: calc(
      100% - 280px - ${(props) => (props.isSidebarOpen ? "420px" : "0px")}
    );
  }

  @media (max-width: 1024px) {
    width: calc(100% - ${(props) => (props.isSidebarOpen ? "420px" : "0px")});
    padding: 0 1rem;
  }

  @media (max-width: 900px) {
    width: 100vw;
  }
`;

const MainContent = styled.div`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  border-radius: 24px;
  box-sizing: border-box;
  height: 100%;
  max-width: 820px;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const Title = styled.h1`
  max-width: 600px;
  text-align: center;
  margin: 0 0 1rem;
  font-size: 2.5rem;
  color: #fff;

  @media (max-width: 1700px) {
    font-size: 1.85rem;
    max-width: 90%;
  }

  @media (max-width: 1250px) {
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    font-size: 1.5rem;
    font-size: 2rem;
    max-width: 100%;
  }

  @media (max-width: 500px) {
    font-size: 1.35rem;
    max-width: 80%;
  }
`;

const Description = styled.p`
  max-width: 600px;
  text-align: center;
  color: #ccc;
  line-height: 24px;
  font-size: 14px;

  @media (max-width: 768px) {
    max-width: 100%;
    font-size: 13px;
  }
`;

const FeatureCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin: 2rem 0 0;
  width: 100%;
  max-width: 650px;

  @media (max-width: 1180px) {
    gap: 0.5rem;
  }

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    margin: 1rem 0 0;
  }
`;

export const Card = styled.div`
  background-color: #424242;
  padding: 0.5rem;
  border-radius: 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: #fff;
  text-decoration: none;
  box-sizing: border-box;
  transition: all 0.3s ease;

  svg {
    color: #00ff00;
    font-size: 1.5rem;
  }

  h2 {
    font-weight: 500;
  }

  :hover {
    background-color: #525252;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 1370px) {
    p {
      font-size: 14px;
    }
  }

  @media (max-width: 1300px) {
    gap: 0;

    p {
      font-size: 14px;
    }
  }

  @media (max-width: 1250px) {
    p {
      font-size: 12px;
    }

    h3 {
      font-size: 16px;
    }
  }

  @media (max-width: 1100px) and (min-width: 768px) {
    gap: 0;
    padding: 0.2rem;

    p {
      font-size: 14px;
    }
  }

  @media (max-width: 480px) {
    gap: 0;

    p {
      font-size: 14px;
    }

    h3 {
      font-size: 16px;
    }
  }
`;

const ChatInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
`;

const ChatInput = styled.textarea`
  flex: 1;
  background: none;
  border: none;
  color: #fff;
  font-size: 0.95rem;
  outline: none;
  font-family: "Inter", sans-serif;
  resize: none;
  min-height: 30px;
  max-height: 120px;
  height: auto;
  overflow-y: auto;
  box-sizing: border-box;
  width: 100%;

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924; /* Track color */
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333; /* Thumb color */
    border-radius: 10px; /* Rounded edges */
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636; /* Color on hover */
  }

  /* Optional: Add a custom arrow */
  &::-webkit-scrollbar-corner {
    background: transparent; /* Corner color */
  }

  &::placeholder {
    color: #666;
  }
`;

const SendButton = styled.button`
  background: #ff6b6b;
  border: none;
  color: #fff;
  border-radius: 20px;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  width: 100%;
  overflow-y: auto;
  margin-bottom: 1rem;
  box-sizing: border-box;
  padding: 0 5px 0 0;

  @media (max-width: 1024px) {
    padding: 0;
  }

  /* Hide default scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1924;
    border: 1px solid #242424;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #333333;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #363636;
  }
`;

const Option = styled.div`
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SeeResults = styled.div`
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  color: #fff;
  font-size: 14px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const RightSidebar = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  right: ${(props) => (props.isOpen ? "0" : `-480px`)};
  width: 480px;
  height: calc(100svh - 80px);
  background-color: #1a1a1a;
  transition: right 0.3s ease-in-out;
  box-sizing: border-box;
  z-index: 1000;

  h3,
  p {
    color: #fff;
  }

  @media (max-width: 1300px) {
    right: ${(props) => (props.isOpen ? "0" : `-420px`)};
    width: 420px;
  }

  @media (max-width: 900px) {
    right: ${(props) => (props.isOpen ? "0" : `-100vw`)};
    width: 100vw;
    height: calc(100svh - 80px);
  }
`;

const HeaderRightbar = styled.div`
  display: flex;
  flex-direction: row;
  height: 80px;
  padding: 0 20px;
  align-items: center;
  box-sizing: border-box;
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.8); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const scaleOut = keyframes`
  from { transform: scale(1); opacity: 1; }
  to { transform: scale(0.8); opacity: 0; }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${(props) => (props.isOpen ? fadeIn : fadeOut)} 0.3s ease-in-out;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  background: white;
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.3);
  position: relative; /* Ensure relative positioning */
  display: flex;
  flex-direction: column;
  animation: ${(props) => (props.isOpen ? scaleIn : scaleOut)} 0.3s ease-in-out;
  transform-origin: center;
`;

const FullScreenButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 15px;
  background: rgba(200, 200, 200, 0.4);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: background 0.3s;

  &:hover {
    background: rgba(200, 200, 200, 0.6);
  }
`;

const MinimizeButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(200, 200, 200, 0.4);
  color: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: 0.3s;

  &:hover {
    background: rgba(200, 200, 200, 0.6);
  }
`;

export default Chat;
